import { Button, HStack, MenuItemProps, Text, VStack } from '@chakra-ui/react';
import { AddIcon, ContentCopyIcon, DeleteIcon, FileIcon, SearchIcon } from 'assets/icons/v2';
import { NoSearchResult } from 'components/common/NoSearchResult';
import { NoTableContent } from 'components/common/NoTableContent';
import { DateCell } from 'components/common/Table/Cells/DateCell';
import { TextCell } from 'components/common/Table/Cells/TextCell';
import { TextCellWithIcon } from 'components/common/Table/Cells/TextCellWithIcon';
import { InputOption } from 'components/common/Table/FilterOptions/InputOption';
import { Filters } from 'components/common/Table/Filters';
import { Pagination } from 'components/common/Table/Pagination';
import { Table } from 'components/common/Table/Table';
import { TextCellProps } from 'components/common/Table/types/Cell';
import { WorksheetTemplateData } from 'components/common/Table/types/Column';
import { NovaIcon } from 'components/ui/novaIcon';
import { WorksheetMenu } from 'components/worksheet/WorksheetMenu';
import { QueryErrorState } from 'components/common/QueryErrorState';
import { ClassesAccessDeniedMessage } from 'components/worksheet/dashboard/ClassesAccessDeniedMessage';
import {
  useDeleteWorksheetTemplate,
  useGetWorksheetTemplates,
  WorksheetTemplateListItem,
} from 'hooks/api/backend/worksheets/useWorksheetTemplates';
import { useIsB2BTeacherOrAdmin } from 'hooks/utils/useIsB2BTeacherOrAdmin';
import { useModalService } from 'hooks/useModalService';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ModalVariant } from 'types/Modal';
import { ProtectedRoutes } from 'types/Routes';
import { formattedDateWithMonthName } from 'utils/formattedDate';
import { createColumns } from 'utils/tableHelper';

type Props = {
  onCreateTemplateClick: () => void;
};

const organizationWorksheetColumns = createColumns<WorksheetTemplateData>()({
  title: {
    header: 'Titel',
    element: (props: TextCellProps) => (
      <TextCellWithIcon
        label={props.label}
        iconBg="#F0EDFE"
        icon={<NovaIcon icon={FileIcon} size="m" color="#4D3C9E" aria-hidden />}
        textStyle="body.s.medium"
        textColor="text.secondary"
      />
    ),
    sortDisabled: true,
  },
  description: {
    header: 'Beschreibung',
    element: (props: TextCellProps) => (
      <TextCell label={props.label} textStyle="body.s.regular" textColor="text.secondary" />
    ),
    sortDisabled: true,
  },
  createdBy: {
    header: 'Erstellt von',
    element: (props: TextCellProps) => (
      <TextCell label={props.label} textStyle="body.s.regular" textColor="text.secondary" />
    ),
    sortDisabled: true,
  },
  createdAt: {
    header: 'Erstellt am',
    element: DateCell,
    sortDisabled: true,
  },
  actionItem: {
    header: 'Aktion',
    sortDisabled: true,
  },
});

export const OrganizationWorksheetsTab = ({ onCreateTemplateClick }: Props): JSX.Element => {
  const navigate = useNavigate();
  const modalService = useModalService();
  const { isB2BUser, isB2BTeacherOrAdmin, isLoading: isAccessLoading } = useIsB2BTeacherOrAdmin();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const templates = useGetWorksheetTemplates({
    page,
    pageSize: 10,
    isDisabled: !isB2BTeacherOrAdmin,
  });
  const { mutateAsync: deleteWorksheetTemplate, isPending: isDeleting } =
    useDeleteWorksheetTemplate();
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const rows = (templates.data?.data || []).filter((template) => {
    if (!normalizedSearch) return true;
    const createdByName = template.createdBy
      ? `${template.createdBy.firstName} ${template.createdBy.lastName}`
      : '';
    return (
      template.title.toLowerCase().includes(normalizedSearch) ||
      (template.description || '').toLowerCase().includes(normalizedSearch) ||
      createdByName.toLowerCase().includes(normalizedSearch)
    );
  });

  const mappedTemplates: WorksheetTemplateData[] = rows.map((template) => {
    const menuItems: {
      icon: React.ReactElement;
      label: string;
      onClick: MenuItemProps['onClick'];
      dangerousAction?: boolean;
    }[] = [
      {
        icon: <NovaIcon icon={ContentCopyIcon} size="s" fill="none" aria-hidden />,
        label: 'Als Arbeitsblatt nutzen',
        onClick: async (e) => {
          e.stopPropagation();
          await handleCopyTemplate(template);
        },
      },
      ...(template.canDelete
        ? [
            {
              icon: <NovaIcon icon={DeleteIcon} size="s" fill="none" aria-hidden />,
              label: 'Löschen',
              dangerousAction: true,
              onClick: (e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                handleDeleteTemplate(template);
              },
            },
          ]
        : []),
    ];

    return {
      id: template.id,
      title: template.title,
      description: template.description || '-',
      createdBy: template.createdBy
        ? `${template.createdBy.firstName} ${template.createdBy.lastName}`
        : '-',
      createdAt: formattedDateWithMonthName(new Date(template.createdAt)) || '-',
      actionItem: <WorksheetMenu items={menuItems} />,
    };
  });

  const handleCopyTemplate = async (template: WorksheetTemplateListItem): Promise<void> => {
    if (!template) return;
    modalService.show(ModalVariant.WORKSHEET_TEMPLATE_COPY, {
      templateId: template.id,
      initialTitle: template.title,
      onCopied: () => undefined,
    });
  };

  const handleTemplateRowClick = (template: WorksheetTemplateListItem): void => {
    if (!template) return;
    navigate(`${ProtectedRoutes.WORKSHEET_TEMPLATE_PREVIEW}/${template.id}`);
  };

  const handleDeleteTemplate = (template: WorksheetTemplateListItem): void => {
    if (!template) return;
    modalService.show(ModalVariant.WORKSHEET_TEMPLATE_DELETE, {
      templateName: template.title,
      onDelete: async () => {
        await deleteWorksheetTemplate({ templateId: template.id });
      },
    });
  };

  const totalPages = templates.data ? Math.ceil(templates.data.total / templates.data.pageSize) : 0;
  const hasTemplates = rows.length > 0;
  const isBusy = isDeleting;
  const showEmptyState = !templates.isLoading && !hasTemplates && !searchQuery;
  const showNoResults = !templates.isLoading && !hasTemplates && !!searchQuery;

  if (isAccessLoading) {
    return (
      <VStack gap="l" align="stretch">
        <Text textStyle="body.s.regular" color="text.tertiary">
          Lade Organisationsvorlagen...
        </Text>
      </VStack>
    );
  }

  if (!isB2BTeacherOrAdmin) {
    return (
      <VStack gap="l" align="stretch">
        <HStack justifyContent="space-between" alignItems="center">
          <Text textStyle="h3.medium" color="text.primary">
            Arbeitsblätter meiner Organisation
          </Text>
        </HStack>
        <ClassesAccessDeniedMessage isB2BUser={isB2BUser} />
      </VStack>
    );
  }

  if (templates.isError) {
    return (
      <QueryErrorState error={templates.error} onRetry={() => templates.refetch()} />
    );
  }

  const renderContent = (): JSX.Element => {
    if (showEmptyState) {
      return (
        <NoTableContent
          title="Noch keine Vorlagen in deiner Organisation."
          description="Erstelle eine Vorlage aus einem deiner Arbeitsblätter."
          buttonText="Vorlage erstellen"
          onClick={onCreateTemplateClick}
        />
      );
    }

    if (showNoResults) {
      return <NoSearchResult query={searchQuery} />;
    }

    return (
      <>
        <Table
          columns={organizationWorksheetColumns}
          data={mappedTemplates}
          isDataLoading={templates.isLoading}
          onRowClick={(id) => {
            const template = rows.find((item) => item.id === id);
            if (!template) return;
            handleTemplateRowClick(template);
          }}
          tableRowProps={{
            cursor: 'pointer',
          }}
        />
        {totalPages > 1 && (
          <Pagination totalPages={totalPages} currentPage={page} onPageChange={setPage} />
        )}
      </>
    );
  };

  return (
    <VStack gap="l" align="stretch">
      <HStack justifyContent="space-between" alignItems="center">
        <Text textStyle="h3.medium" color="text.primary">
          Arbeitsblätter meiner Organisation
        </Text>
        <Button onClick={onCreateTemplateClick} disabled={isBusy}>
          <NovaIcon icon={AddIcon} size="m" aria-hidden />
          Vorlage erstellen
        </Button>
      </HStack>

      {!showEmptyState && (
        <Filters
          filters={[
            [
              {
                id: 'template-search',
                element: InputOption,
                placeholder: 'Suche nach Vorlagen',
                value: searchQuery,
                options: [],
                onChange: setSearchQuery,
                icon: <NovaIcon icon={SearchIcon} size="m" aria-hidden />,
                onClear: () => setSearchQuery(''),
              },
            ],
          ]}
        />
      )}

      <Text textStyle="body.s.regular" color="text.tertiary">
        {templates.data?.total ?? 0} Vorlagen in der Liste
      </Text>

      {renderContent()}
    </VStack>
  );
};
